import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFetchProviders } from '@/services/provider/provider.type';
import {
  ApiResponse,
  CreateProvider,
  SelectedProvider,
  DeleteProvider,
  Provider,
  ProviderApiPayload,
} from '../sagas/providerSaga';

export interface ProviderState {
  providers: ApiResponse;
  loading: boolean;
  error: boolean;
  provider: Provider;
  selectedProvider: SelectedProvider;
  shouldRefetchProviders: boolean;
  message?: string;
  createdProvider: any;
  updateSuccess: boolean;
}

export const initialState: ProviderState = {
  providers: {
    data: [],
    pagination: {
      totalRecords: 0,
      totalItems: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
    success: false,
    message: '',
  },
  loading: false,
  error: false,
  provider: {
    providerId: '',
    partnerId: '',
    name: '',
    createdOn: '',
    updatedOn: '',
    server: '',
    profile: '',
    configs: '',
    vdiVersion: '',
    syncVdi: false,
  },
  createdProvider: {
    success: false,
    message: '',
    data: {},
    error: false,
  },
  selectedProvider: {
    name: '',
    id: '',
  },
  shouldRefetchProviders: false,
  updateSuccess: false,
};

const providerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    getProviders: (state, action: PayloadAction<IFetchProviders>) => {
      state.loading = true;
      state.error = false;
      state.message = '';
    },
    getProvidersSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.error = false;
      state.message = action.payload.message;
      state.providers = { ...action.payload };
    },
    getProvidersFailure: (state, action: any) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.messageDetails?.length
        ? action.payload?.messageDetails[0]?.message
        : action.payload?.message;
    },
    getProvider: (state, _action: PayloadAction<string>) => {
      state.loading = true;
    },
    getProviderSuccess: (state, action: PayloadAction<Provider>) => {
      state.loading = false;
      state.provider = { ...action.payload }; // Avoid direct assignment, create a new object
    },
    getProviderFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    createProvider: (state, action: PayloadAction<CreateProvider>) => {
      state.loading = true;
    },
    createProviderSuccess: (
      state,
      action: PayloadAction<ProviderApiPayload>,
    ) => {
      state.loading = false;
      state.createdProvider = { ...action.payload }; // Avoid direct assignment, create a new object
      state.shouldRefetchProviders = true;
    },
    createProviderFailure: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.error = true;
      state.createdProvider.message = action?.payload?.message;
      state.createdProvider.error = true;
      state.createdProvider.success = false;
    },
    updateProvider: (state, action: PayloadAction<CreateProvider>) => {
      state.loading = true;
      state.error = false;
      state.updateSuccess = false;
      state.provider = { ...initialState.provider };
    },
    updateProviderSuccess: (state, action: PayloadAction<Provider>) => {
      state.loading = false;
      state.updateSuccess = true;
      state.provider = { ...action.payload };
      state.shouldRefetchProviders = true;
    },
    updateProviderFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    selectedProvider: (state, action: PayloadAction<SelectedProvider>) => {
      state.loading = false;
      state = {
        ...state,
        selectedProvider: { ...action.payload },
      };
    },
    deleteProvider: (state, action: PayloadAction<DeleteProvider>) => {
      state.loading = true;
    },
    deleteProviderSuccess: (state, action: PayloadAction<Provider>) => {
      state.loading = false;
      state = {
        ...state,
        providers: {
          ...state.providers,
          data: state.providers.data.filter(
            (item) => item.providerId !== action.payload.providerId,
          ),
        },
      }; // Avoid direct assignment, create a new object
      state.shouldRefetchProviders = true;
    },
    deleteProviderFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    activateProvider: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    activateProviderSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.message = action.payload?.message || 'Provider activated successfully.';
      state.shouldRefetchProviders = true;
    },
    activateProviderFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to activate provider.';
    },
    setShouldRefetchProviders: (state, action: PayloadAction<boolean>) => {
      state.shouldRefetchProviders = action.payload;
    },
    clearProviderError: (state) => {
      state.error = false;
      state.message = ''; // Reset message to empty string as per initial state
    },
    clearCreatedProviderData: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.error = false;
      state.createdProvider.message = '';
      state.createdProvider.error = false;
      state.createdProvider.success = false;
    },
    clearUpdatedProviderData: (state) => {
      state.updateSuccess = false;
      state.error = false;
    },
  },
});

const providerReducer = providerSlice.reducer;
export const {
  getProviders,
  getProvidersSuccess,
  getProvidersFailure,
  getProvider,
  getProviderSuccess,
  getProviderFailure,
  createProvider,
  createProviderSuccess,
  createProviderFailure,
  updateProvider,
  updateProviderSuccess,
  updateProviderFailure,
  selectedProvider,
  deleteProvider,
  deleteProviderSuccess,
  deleteProviderFailure,
  activateProvider,
  activateProviderSuccess,
  activateProviderFailure,
  setShouldRefetchProviders,
  clearProviderError,
  clearCreatedProviderData,
  clearUpdatedProviderData,
} = providerSlice.actions;
export default providerReducer;
